import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageCopy } from '../lib/copy';
import { SHOW_CAREERS } from '../lib/features';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePageTitle } from '../hooks/usePageTitle';

const ABOUT_DEFAULTS = {
  headline:      'Filmmaker and creative producer.',
  location:      'Based in Lisbon. Available for projects across Europe and beyond.',
  story_left:    "Having worked across media agencies managing accounts for some of the world's biggest brands, I developed a deep understanding of how audiences think, how channels work, and how budgets need to perform.\n\nThat foundation led me to Banjoman, Ireland's leading production company, where as Head of The Studio I honed my craft across every stage of creative production. Producing, directing, and editing commercial films for some of Ireland's most recognised brands, I learned what it takes to make work that's both creatively strong and commercially effective.",
  story_right:   "Liam Boyd Creative Studio was born when I relocated to Lisbon and stepped out on my own. The studio exists to bring that same level of craft and strategic thinking to brands and agencies globally. World-class commercial film, built around your brief.",
  approach_left: "I can work with you at any stage of the production process. If you've just received a brief, if you have an idea but aren't sure where to start, or if you've already shot something beautiful and need someone to bring it to life, I'm here.\n\nMy clients can expect award-winning production quality, alongside an understanding that creative productions sometimes need to be lean and agile. Having produced large multi-week productions across multiple countries as well as super-localised social-first content, you're covered whatever the scale.",
  approach_right: "The most important thing is the work. Delivering high-quality, well-crafted creative is always the priority. But working with genuinely good people, people you want to come back to again and again, is a close second.\n\nBeing the steward of your brand is an honour, and I treat it as one. Your brand is in good hands here.",
};

function Text({ value }) {
  return value.split('\n\n').map((para, i, arr) => (
    <React.Fragment key={i}>{para}{i < arr.length - 1 && <><br /><br /></>}</React.Fragment>
  ));
}

export default function About() {
  const [copy, setCopy] = useState(ABOUT_DEFAULTS);
  const isMobile = useIsMobile();
  usePageTitle('About');

  useEffect(() => {
    fetchPageCopy('about').then(c => {
      if (Object.keys(c).length > 0) setCopy(prev => ({ ...prev, ...c }));
    });
  }, []);

  return (
    <div className="page-fade" style={{ paddingTop: '56px' }}>
      <main style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '3rem 1.5rem 5rem' : '5rem 3rem 8rem' }}>

        {/* Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '2rem' : '6rem',
          marginBottom: isMobile ? '3.5rem' : '6rem',
          paddingBottom: isMobile ? '3.5rem' : '6rem',
          borderBottom: '1px solid var(--rule)',
        }}>
          <div>
            <p style={{ fontSize: '11px', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: '2rem' }}>
              About
            </p>
            <h1 style={{ fontSize: isMobile ? '32px' : '52px', fontWeight: 300, lineHeight: 1.15, color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              {copy.headline}
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <p style={{ fontSize: '14px', fontWeight: 300, color: 'var(--ink-mid)', lineHeight: 1.85 }}>
              {copy.location}
            </p>
          </div>
        </div>

        {/* Story */}
        <div style={{ marginBottom: isMobile ? '3.5rem' : '6rem', paddingBottom: isMobile ? '3.5rem' : '6rem', borderBottom: '1px solid var(--rule)' }}>
          <p style={{ fontSize: '11px', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: '2rem' }}>
            Story
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '1.5rem' : '6rem' }}>
            <p style={{ fontSize: '15px', fontWeight: 300, color: 'var(--ink-mid)', lineHeight: 1.9 }}>
              <Text value={copy.story_left} />
            </p>
            <p style={{ fontSize: '15px', fontWeight: 300, color: 'var(--ink-mid)', lineHeight: 1.9 }}>
              <Text value={copy.story_right} />
            </p>
          </div>
        </div>

        {/* Approach */}
        <div style={{ marginBottom: isMobile ? '3.5rem' : '6rem', paddingBottom: isMobile ? '3.5rem' : '6rem', borderBottom: '1px solid var(--rule)' }}>
          <p style={{ fontSize: '11px', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: '2rem' }}>
            Approach
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '1.5rem' : '6rem' }}>
            <p style={{ fontSize: '15px', fontWeight: 300, color: 'var(--ink-mid)', lineHeight: 1.9 }}>
              <Text value={copy.approach_left} />
            </p>
            <p style={{ fontSize: '15px', fontWeight: 300, color: 'var(--ink-mid)', lineHeight: 1.9 }}>
              <Text value={copy.approach_right} />
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <a href="mailto:liam@liamboyd.com" style={{ fontSize: '15px', fontWeight: 300, color: 'var(--ink)', borderBottom: '1px solid var(--ink)', paddingBottom: '2px', transition: 'opacity 150ms' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.5'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            liam@liamboyd.com
          </a>
          {SHOW_CAREERS && (
            <Link to="/careers" style={{ fontSize: '12px', fontWeight: 300, color: 'var(--ink-faint)', letterSpacing: '0.04em' }}>
              View career history →
            </Link>
          )}
        </div>

      </main>
    </div>
  );
}
